import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGeolocation } from './useGeolocation';
import { storeService } from '../services/storeService';
import { Store } from '../types';
import { calculateDistance } from '../utils/distanceCalculator';

export const useStores = () => {
  const { location, error: locationError } = useGeolocation();
  const [stores, setStores] = useState<Store[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { data: rawStores, isLoading: isFetching } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      if (!location) {
        throw new Error('Location not available');
      }
      return storeService.getNearbyStores(location);
    },
    enabled: !!location,
  });

  useEffect(() => {
    if (rawStores && location) {
      try {
        const storesWithDistance = rawStores.map(store => ({
          ...store,
          distance: calculateDistance(location.latitude, location.longitude, store.latitude, store.longitude),
        }));

        // Sort by distance
        const sortedStores = storesWithDistance.sort((a, b) => a.distance - b.distance);
        setStores(sortedStores);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to process stores');
      }
    }
  }, [rawStores, location]);

  return {
    stores,
    isLoading: isLoading || isFetching,
    error: error || locationError,
  };
};
